import * as React from 'react';

// API
import apibridge from '../../apibridge';

// UTILS
import { validationErrorsToString } from '../../utils';

// REDUX
import { useAppDispatch, useAppSelector } from '../../store';
import UserSlice from '../../store/slices/User';

// PACKAGES
import { Link, useLocation } from 'react-router-dom';
import { SwitchTransition, CSSTransition } from 'react-transition-group';

// COMPONENTS
import ScrollToTopButton from '../../components/ScrollToTopButton';
import StatusIcon from '../../components/StatusIcon';
import Button from '../../components/Button';
import LoadingSpinner from '../../components/LoadingSpinner';

// ICONS
import { ReactComponent as PlusIcon } from '../../assets/svg/plus.svg';
import { ReactComponent as SwitchUserIcon } from '../../assets/svg/switch-user.svg';
import { ReactComponent as SwitchIcon } from '../../assets/svg/switch.svg';
import { ReactComponent as DeviceIcon } from '../../assets/svg/device.svg';

// TYPES
import {
	SafetyChecklistAdminDashboardChecklist,
	SafetyChecklistUserDashboardChecklist,
	DomainChecklistStatusType
} from '../../api/models';

const SafetyChecklists: React.FC = () => {
	const dispatch = useAppDispatch();
	const location = useLocation();

	// Error and Loading state
	const [formErrorMessage, setFormErrorMessage] = React.useState<string>('');

	const [isLoading, setIsLoading] = React.useState<boolean>(true);

	// Check if Organisation is active
	const isOrganisationActive = useAppSelector((state) => state.user.isOrganisationActive);

	// Check if User is Organisation Admin
	const isOrganisationAdmin = useAppSelector((state) => state.user.isOrganisationAdmin);

	// State to manage maximum number of items per section
	const defaultMaxItems: number = 5;

	// Check if User is viewing Checklist as an Admin or not
	const viewChecklistAsAdmin = useAppSelector((state) => state.user.viewChecklistAsAdmin);

	// SET UP ADMIN DATA STATE AND INITIAL ADMIN DATA
	const initialAdminData: SafetyChecklistAdminDashboardChecklist = {
		inProgressList: [],
		completedList: []
	};

	const [adminSafetyChecklistsData, setAdminSafetyChecklistsData] =
		React.useState<SafetyChecklistAdminDashboardChecklist>(initialAdminData);

	const getAdminSafetyChecklistsData = async () => {
		setIsLoading(true);

		const response = await apibridge.getAdminSafetyChecklistDashboard();

		response.result = response.result || initialAdminData;

		if (response.isError) {
			setIsLoading(false);

			setFormErrorMessage(
				response.validationErrors.length
					? validationErrorsToString(response.validationErrors)
					: response.message || 'An error has occurred'
			);
		} else {
			setIsLoading(false);

			setAdminSafetyChecklistsData(response.result);
		}
	};

	// SET UP USER DATA STATE AND INITIAL USER DATA
	const initialUserData: SafetyChecklistUserDashboardChecklist = {
		myList: [],
		approvalList: [],
		completedList: []
	};

	const [userSafetyChecklistsData, setUserSafetyChecklistsData] =
		React.useState<SafetyChecklistUserDashboardChecklist>(initialUserData);

	const getUserSafetyChecklistsData = async () => {
		setIsLoading(true);

		const response = await apibridge.getUserSafetyChecklistDashboard();

		response.result = response.result || initialUserData;

		if (response.isError) {
			setIsLoading(false);

			setFormErrorMessage(
				response.validationErrors.length
					? validationErrorsToString(response.validationErrors)
					: response.message || 'An error has occurred'
			);
		} else {
			setIsLoading(false);

			setUserSafetyChecklistsData(response.result);
		}
	};

	// Check if User follow the user-view Link from Dashboard
	React.useEffect(() => {
		if (location.pathname === '/safety-checklists/user-view') {
			dispatch(UserSlice.actions.update({ viewChecklistAsAdmin: false }));

			getUserSafetyChecklistsData();
		} else if (isOrganisationAdmin && location.pathname === '/safety-checklists/admin-view') {
			dispatch(UserSlice.actions.update({ viewChecklistAsAdmin: true }));

			getAdminSafetyChecklistsData();
		}
	}, []);

	// Load data based on which view is loaded
	React.useEffect(() => {
		if (isOrganisationAdmin && viewChecklistAsAdmin) {
			getAdminSafetyChecklistsData();
		} else {
			getUserSafetyChecklistsData();
		}

		setIsLoading(false);
	}, []);

	// Filter Admin Data
	const inProgressAdminSafetyChecklistsData = adminSafetyChecklistsData?.inProgressList;
	const completedAdminSafetyChecklistsData = adminSafetyChecklistsData?.completedList;

	const [maxInProgressAdminSafetyChecklists, setMaxInProgressAdminSafetyChecklists] =
		React.useState<number>(defaultMaxItems);
	const [maxCompletedAdminSafetyChecklists, setMaxCompletedAdminSafetyChecklists] =
		React.useState<number>(defaultMaxItems);

	// Filter User Data
	const myListUserSafetyChecklistsData = userSafetyChecklistsData?.myList;
	const approvalUserSafetyChecklistsData = userSafetyChecklistsData?.approvalList;
	const completedUserSafetyChecklistsData = userSafetyChecklistsData?.completedList;

	const [maxMyListUserSafetyChecklistsData, setMaxMyListUserSafetyChecklistsData] =
		React.useState<number>(defaultMaxItems);
	const [maxApprovalUserSafetyChecklistsData, setMaxApprovalUserSafetyChecklistsData] =
		React.useState<number>(defaultMaxItems);
	const [maxcompletedUserSafetyChecklistsData, setMaxcompletedUserSafetyChecklistsData] =
		React.useState<number>(defaultMaxItems);

	// SWITCH ROLE FUNCTION
	const switchRole = () => {
		if (isOrganisationAdmin) {
			if (viewChecklistAsAdmin) {
				dispatch(UserSlice.actions.update({ viewChecklistAsAdmin: false }));

				getUserSafetyChecklistsData();
			} else {
				dispatch(UserSlice.actions.update({ viewChecklistAsAdmin: true }));

				getAdminSafetyChecklistsData();
			}
		}
	};

	const displayStatus = (status?: string) => {
		if (status) {
			if (status === DomainChecklistStatusType.InProgress) {
				return 'In Progress';
			} else if (status === DomainChecklistStatusType.AwaitingApproval) {
				return 'Awaiting approval';
			} else if (status === DomainChecklistStatusType.Approved) {
				return 'Approved';
			} else {
				return status;
			}
		}
	};

	return (
		<div className="page-general-layout satefy-checklist">
			{!!formErrorMessage ? (
				<div className="form-validation-error">{formErrorMessage}</div>
			) : (
				<SwitchTransition mode="out-in">
					<CSSTransition key={`key_${isLoading}`} timeout={600} classNames="standalone-fade" mountOnEnter unmountOnExit>
						{isLoading ? (
							<LoadingSpinner show={isLoading} />
						) : (
							<>
								{isOrganisationAdmin && viewChecklistAsAdmin ? (
									<>
										<p className="sub-grey-text mbtom-30">
											Safety checklists are a critical risk management tool to manage safety and minimise the risk of
											harm to children in your organisation. Below, you can view all safety checklists created
											throughout your organisation and create a checklist.
										</p>

										<div className="general-notification-box">
											<div className="box-left">
												<SwitchUserIcon />

												<div className="info-text">
													<p className="mbtom-8">
														<strong>As a Leader</strong> you can view all safety checklists{' '}
														<strong>created throughout your organisation</strong>.
													</p>

													<p>
														You can also view safety checklists <strong>created by you</strong> or{' '}
														<strong>assigned to you</strong> for approval. Click on ‘View my checklists’ to switch
														roles.
													</p>
												</div>
											</div>

											<Button className="btn-primary small" onClick={() => switchRole()}>
												<SwitchIcon className="link-icon" />
												View my checklists
											</Button>
										</div>

										{isOrganisationActive && (
											<div
												className={
													inProgressAdminSafetyChecklistsData?.length
														? 'submit-btn-container'
														: 'submit-btn-container empty'
												}
											>
												<Link to="/new-checklist" className="btn-primary submit-btn">
													Create a checklist
												</Link>
											</div>
										)}

										<section className="info-section complaints-page">
											<div className="section-header bold">Checklists in progress</div>

											{inProgressAdminSafetyChecklistsData && inProgressAdminSafetyChecklistsData.length > 0 ? (
												<>
													{inProgressAdminSafetyChecklistsData
														.slice(0, maxInProgressAdminSafetyChecklists)
														.map((item) => {
															return (
																<div key={item.id} className="row-container">
																	<div className="row detail-row">
																		<div className="col-auto pdl-0 pdr-0">
																			<StatusIcon
																				statusType={
																					item.status === DomainChecklistStatusType.AwaitingApproval
																						? 'danger'
																						: item.status === DomainChecklistStatusType.InProgress
																						? 'warning'
																						: 'success'
																				}
																			/>
																		</div>

																		<div className="col-md-auto full-width">
																			<div className="row detail-info">
																				<div className="col-lg-4 detail-name">{item.purpose}</div>

																				<div className="col-lg-4 detail-status">
																					<div className="title">Submitted by</div>

																					<div className="name">{item.submitter}</div>
																				</div>

																				<div className="col-lg-4 detail-status">
																					<div className="title">Status</div>

																					<div className="status">{displayStatus(item.status)}</div>
																				</div>
																			</div>
																		</div>

																		<div className="col-auto pdr-0 link-group">
																			<Link className="btn-text complaints-page" to={`/safety-checklists/${item.id}`}>
																				View
																			</Link>
																		</div>
																	</div>
																</div>
															);
														})}

													{maxInProgressAdminSafetyChecklists < inProgressAdminSafetyChecklistsData.length && (
														<Button
															className="btn-secondary btn-show-more"
															onClick={() =>
																setMaxInProgressAdminSafetyChecklists(inProgressAdminSafetyChecklistsData.length)
															}
															buttonIcon={<PlusIcon className="link-icon" />}
														>
															Show {inProgressAdminSafetyChecklistsData.length - maxInProgressAdminSafetyChecklists}{' '}
															more
														</Button>
													)}
												</>
											) : (
												<div className="empty-notification">
													<p className="sub-grey-text mbtom-24">You have no active checklists.</p>

													<p className="sub-grey-text">To create a checklist click ‘Create a checklist’.</p>

													<p className="sub-grey-text">
														You can choose to customise a checklist template to suit your needs or create an entirely
														new checklist.
													</p>
												</div>
											)}
										</section>

										<section className="info-section complaints-page">
											<div className="section-header bold">Completed checklists</div>

											{completedAdminSafetyChecklistsData && completedAdminSafetyChecklistsData.length > 0 ? (
												<>
													{completedAdminSafetyChecklistsData
														.slice(0, maxCompletedAdminSafetyChecklists)
														.map((item) => {
															return (
																<div key={item.id} className="row-container">
																	<div className="row detail-row">
																		<div className="col-auto pdl-0 pdr-0">
																			<StatusIcon
																				statusType={
																					item.status === DomainChecklistStatusType.AwaitingApproval
																						? 'danger'
																						: item.status === DomainChecklistStatusType.InProgress
																						? 'warning'
																						: 'success'
																				}
																			/>
																		</div>

																		<div className="col-md-auto full-width">
																			<div className="row detail-info">
																				<div className="col-lg-4 detail-name">{item.purpose}</div>

																				<div className="col-lg-4 detail-status">
																					<div className="title">Approved by</div>

																					<div className="name">{item.approver}</div>
																				</div>

																				<div className="col-lg-4 detail-status">
																					<div className="title">Status</div>

																					<div className="status">{displayStatus(item.status)}</div>
																				</div>
																			</div>
																		</div>

																		<div className="col-auto pdr-0 link-group">
																			<Link className="btn-text complaints-page" to={`/safety-checklists/${item.id}`}>
																				View
																			</Link>
																		</div>
																	</div>
																</div>
															);
														})}

													{maxCompletedAdminSafetyChecklists < completedAdminSafetyChecklistsData.length && (
														<Button
															className="btn-secondary btn-show-more"
															onClick={() =>
																setMaxCompletedAdminSafetyChecklists(completedAdminSafetyChecklistsData.length)
															}
															buttonIcon={<PlusIcon className="link-icon" />}
														>
															Show {completedAdminSafetyChecklistsData.length - maxCompletedAdminSafetyChecklists} more
														</Button>
													)}
												</>
											) : (
												<div className="empty-notification">
													<p className="sub-grey-text">You have no completed checklists.</p>
												</div>
											)}
										</section>
									</>
								) : (
									<>
										<p className="sub-grey-text mbtom-30">
											Safety checklists are a critical risk management tool to manage safety and minimise the risk of
											harm to children in your organisation. You can create checklists, view your checklists and approve
											checklists assigned to you.
										</p>

										{isOrganisationAdmin && (
											<div className="general-notification-box">
												<div className="box-left">
													<SwitchUserIcon />

													<div className="info-text">
														<p className="mbtom-8">
															Below are safety checklists <strong>created by you</strong> or{' '}
															<strong>assigned to you</strong> for approval.{' '}
														</p>

														<p>
															As a <strong>Leader</strong>, you can also view all safety checklists created throughout
															your organisation. Click on ‘View all checklists’ to switch roles.
														</p>
													</div>
												</div>

												<Button className="btn-primary small" onClick={() => switchRole()}>
													<SwitchIcon className="link-icon" />
													View all checklists
												</Button>
											</div>
										)}

										{isOrganisationActive && (
											<div
												className={
													inProgressAdminSafetyChecklistsData?.length
														? 'submit-btn-container'
														: 'submit-btn-container empty'
												}
											>
												<Link to="/new-checklist" className="btn-primary submit-btn">
													Create a checklist
												</Link>
											</div>
										)}

										<section className="info-section complaints-page">
											<div className="section-header bold">My active checklists</div>

											{myListUserSafetyChecklistsData && myListUserSafetyChecklistsData.length > 0 ? (
												<>
													{myListUserSafetyChecklistsData.slice(0, maxMyListUserSafetyChecklistsData).map((item) => {
														return (
															<div key={item.id} className="row-container">
																<div className="row detail-row">
																	<div className="col-auto pdl-0 pdr-0">
																		<StatusIcon
																			statusType={
																				item.status === DomainChecklistStatusType.AwaitingApproval
																					? 'danger'
																					: item.status === DomainChecklistStatusType.InProgress
																					? 'warning'
																					: 'success'
																			}
																		/>
																	</div>

																	<div className="col-md-auto full-width">
																		<div className="row detail-info">
																			<div className="col-lg-4 detail-name">{item.purpose}</div>

																			<div className="col-lg-4 detail-status">
																				<div className="title">Approval by</div>

																				<div className="name">{item.approver}</div>
																			</div>

																			<div className="col-lg-4 detail-status">
																				<div className="title">Status</div>

																				<div className="status">{displayStatus(item.status)}</div>
																			</div>
																		</div>
																	</div>

																	<div className="col-auto pdr-0 link-group">
																		<Link className="btn-text complaints-page" to={`/safety-checklists/${item.id}`}>
																			View
																		</Link>
																	</div>
																</div>
															</div>
														);
													})}

													{maxMyListUserSafetyChecklistsData < myListUserSafetyChecklistsData.length && (
														<Button
															className="btn-secondary btn-show-more"
															onClick={() =>
																setMaxMyListUserSafetyChecklistsData(myListUserSafetyChecklistsData.length)
															}
															buttonIcon={<PlusIcon className="link-icon" />}
														>
															Show {myListUserSafetyChecklistsData.length - maxMyListUserSafetyChecklistsData} more
														</Button>
													)}
												</>
											) : (
												<div className="empty-notification">
													<p className="sub-grey-text mbtom-24">You have no active checklists.</p>

													<p className="sub-grey-text">To create a checklist, select ‘Create new checklist.</p>

													<p className="sub-grey-text">
														You can choose to edit a checklist to suit your needs, or create a new checklist from
														scratch.
													</p>
												</div>
											)}
										</section>

										<section className="info-section complaints-page">
											<div className="section-header bold">Submitted for my approval</div>

											{approvalUserSafetyChecklistsData && approvalUserSafetyChecklistsData.length > 0 ? (
												<>
													{approvalUserSafetyChecklistsData
														.slice(0, maxApprovalUserSafetyChecklistsData)
														.map((item) => {
															return (
																<div key={item.id} className="row-container">
																	<div className="row detail-row">
																		<div className="col-auto pdl-0 pdr-0">
																			<StatusIcon
																				statusType={
																					item.status === DomainChecklistStatusType.AwaitingApproval
																						? 'danger'
																						: item.status === DomainChecklistStatusType.InProgress
																						? 'warning'
																						: 'success'
																				}
																			/>
																		</div>

																		<div className="col-md-auto full-width">
																			<div className="row detail-info">
																				<div className="col-lg-4 detail-name">{item.purpose}</div>

																				<div className="col-lg-4 detail-status">
																					<div className="title">Submitted by</div>

																					<div className="name">{item.submitter}</div>
																				</div>

																				<div className="col-lg-4 detail-status">
																					<div className="title">Status</div>

																					<div className="status">{displayStatus(item.status)}</div>
																				</div>
																			</div>
																		</div>

																		<div className="col-auto pdr-0 link-group">
																			<Link className="btn-text complaints-page" to={`/safety-checklists/${item.id}`}>
																				View
																			</Link>
																		</div>
																	</div>
																</div>
															);
														})}

													{maxApprovalUserSafetyChecklistsData < approvalUserSafetyChecklistsData.length && (
														<Button
															className="btn-secondary btn-show-more"
															onClick={() =>
																setMaxApprovalUserSafetyChecklistsData(approvalUserSafetyChecklistsData.length)
															}
															buttonIcon={<PlusIcon className="link-icon" />}
														>
															Show {approvalUserSafetyChecklistsData.length - maxApprovalUserSafetyChecklistsData} more
														</Button>
													)}
												</>
											) : (
												<div className="empty-notification">
													<p className="sub-grey-text">You have no checklists to approve.</p>
												</div>
											)}
										</section>

										<section className="info-section complaints-page">
											<div className="section-header bold">My completed checklists</div>

											{completedUserSafetyChecklistsData && completedUserSafetyChecklistsData.length > 0 ? (
												<>
													{completedUserSafetyChecklistsData
														.slice(0, maxcompletedUserSafetyChecklistsData)
														.map((item) => {
															return (
																<div key={item.id} className="row-container">
																	<div className="row detail-row">
																		<div className="col-auto pdl-0 pdr-0">
																			<StatusIcon
																				statusType={
																					item.status === DomainChecklistStatusType.AwaitingApproval
																						? 'danger'
																						: item.status === DomainChecklistStatusType.InProgress
																						? 'warning'
																						: 'success'
																				}
																			/>
																		</div>

																		<div className="col-md-auto full-width">
																			<div className="row detail-info">
																				<div className="col-lg-4 detail-name">{item.purpose}</div>

																				<div className="col-lg-4 detail-status">
																					<div className="title">Approval by</div>

																					<div className="name">{item.approver}</div>
																				</div>

																				<div className="col-lg-4 detail-status">
																					<div className="title">Status</div>

																					<div className="status">{displayStatus(item.status)}</div>
																				</div>
																			</div>
																		</div>

																		<div className="col-auto pdr-0 link-group">
																			<Link className="btn-text complaints-page" to={`/safety-checklists/${item.id}`}>
																				View
																			</Link>
																		</div>
																	</div>
																</div>
															);
														})}

													{maxcompletedUserSafetyChecklistsData < completedUserSafetyChecklistsData.length && (
														<Button
															className="btn-secondary btn-show-more"
															onClick={() =>
																setMaxcompletedUserSafetyChecklistsData(completedUserSafetyChecklistsData.length)
															}
															buttonIcon={<PlusIcon className="link-icon" />}
														>
															Show {completedUserSafetyChecklistsData.length - maxcompletedUserSafetyChecklistsData}{' '}
															more
														</Button>
													)}
												</>
											) : (
												<div className="empty-notification">
													<p className="sub-grey-text">You have no completed checklists.</p>
												</div>
											)}
										</section>
									</>
								)}

								<ScrollToTopButton offsetValue={100} />
							</>
						)}
					</CSSTransition>
				</SwitchTransition>
			)}
		</div>
	);
};

export default SafetyChecklists;
